import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormLabel,
  Input,
  RadioGroup,
  Radio,
  Stack,
  FormControl,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useEffect } from 'react';
import { useCreateContentEntry, useUpdateContentEntry } from '@src/modules/settings/context/settings-api-hooks';
import { DateTime } from 'luxon';

const schema = yup.object().shape({
  title: yup.string().required('Title is required'),
  releaseDate: yup.date().required('Date is required').typeError('Date is required'),
  dateFormat: yup.string().oneOf(['MY', 'DMY']).required(),
  message: yup.string(),
});

function EventEdit({
  isOpen, onClose, selectedEvent,
}) {
  const { mutateAsync: doCreateContentEntry, isLoading: isCreating } = useCreateContentEntry('coming-soon');
  const { mutateAsync: doUpdateContentEntry, isLoading: isUpdating } = useUpdateContentEntry('coming-soon');

  const modalTitle = selectedEvent ? 'Update Event' : 'Add Event';
  const submitButtonLabel = selectedEvent ? 'Update Event' : 'Save Event';

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: '',
      releaseDate: '',
      dateFormat: 'MY',
      message: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    reset({
      title: selectedEvent?.title || '',
      releaseDate: selectedEvent?.releaseDate || '',
      dateFormat: selectedEvent?.dateFormat || 'MY',
      message: selectedEvent?.message || '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent]);

  const handleClose = () => {
    reset({
      title: '',
      releaseDate: '',
      dateFormat: 'MY',
      message: '',
    });
    onClose();
  };

  const onSubmit = async (data) => {
    const formData = {
      ...data,
      releaseDate: DateTime.fromJSDate(data.releaseDate).toFormat('yyyy-MM-dd'),
      section: 'Homepage',
      itemType: 8,
    };

    if (!formData.message) delete formData.message;

    if (selectedEvent) {
      await doUpdateContentEntry({ id: selectedEvent.id, ...formData });
    } else {
      await doCreateContentEntry(formData);
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" trapFocus={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form id="event-form" onSubmit={handleSubmit(onSubmit)}>
            <FormControl mb={4} isInvalid={errors.title}>
              <FormLabel>Title</FormLabel>
              <Input placeholder="Event title" {...register('title')} />
              <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={errors.releaseDate} width="200px">
              <FormLabel>Date</FormLabel>
              <Controller
                control={control}
                name="releaseDate"
                render={({ field }) => (
                  <Input
                    type="date"
                    min={DateTime.local().toISODate()}
                    value={field.value ? DateTime.fromISO(field.value).toISODate() : ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
              />
              <FormErrorMessage>{errors.releaseDate?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={errors.dateFormat}>
              <FormLabel>Displayed Date Format</FormLabel>
              <Controller
                control={control}
                name="dateFormat"
                render={({ field }) => (
                  <RadioGroup {...field}>
                    <Stack direction="row">
                      <Radio value="MY">Month Year</Radio>
                      <Radio value="DMY">Day Month Year</Radio>
                    </Stack>
                  </RadioGroup>
                )}
              />
              <FormErrorMessage>{errors.dateFormat?.message}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={errors.message}>
              <FormLabel>Description</FormLabel>
              <Controller
                control={control}
                name="message"
                render={({ field: { onChange, value } }) => (
                  <CKEditor
                    editor={ClassicEditor}
                    config={{
                      toolbar: ['bold', 'italic', 'link'],
                      link: {
                        decorators: {
                          openInNewTab: {
                            mode: 'manual',
                            label: 'Open in a new tab',
                            attributes: {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            },
                          },
                        },
                      },
                    }}
                    data={value}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      onChange(data);
                    }}
                  />
                )}
              />
              <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
            </FormControl>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="event-form"
            isLoading={isCreating || isUpdating}
          >
            {submitButtonLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default EventEdit;
